import { storedFloor } from '../Provider/payFloors';

export const formatPay = (type, amt) => {
  // Handle null, undefined, or non-numeric values
  if (amt === null || amt === undefined || isNaN(parseFloat(amt))) {
    return '—';
  }
  
  // Convert to number if it's a string
  const amount = typeof amt === 'string' ? parseFloat(amt) : amt;
  
  // Handle type as both string (legacy) and array (new multi-select)
  const typeStr = Array.isArray(type) 
    ? type.join(',').toLowerCase() 
    : (type || '').toLowerCase();
  
  // Format based on type
  if (typeStr.includes('hour')) {
    return `$${amount}/hr`;
  } else {
    return `$${Math.round(amount / 1000)}K/yr`;
  }
};

// Values that carry no real location. '' and null arrive straight from the API
// (the backend sanitizer passes locationPreference through untouched), 'N/A'
// and '—' are legacy placeholders that reached callers as literal data, and
// 'Flexible' is injected upstream by normalizeProviderProfileForCard, which the
// group and admin decks run before the card ever sees the profile. Treating
// them all as unset keeps every surface saying the same thing.
const UNSET_LOCATIONS = ['n/a', 'na', 'none', 'null', 'undefined', 'flexible', '—', '-'];

const cleanLocation = (value) => (typeof value === 'string' ? value.trim() : '');

const hasRealLocation = (value) =>
  !!value && !UNSET_LOCATIONS.includes(value.toLowerCase());

/**
 * Builds the card's location pill: "<place> (<radius>)".
 *
 * Coalesces rather than relying on a destructuring default, because a
 * destructuring default fires only for undefined. An empty-string location (a
 * real, common profile state) previously rendered a bare " (100 mi)" on the
 * recruiter deck, and a non-numeric radius rendered "— mi" / "Anywhere mi".
 * Each half is dropped when it says nothing, so the pill never shows a radius
 * with nothing to anchor it to.
 *
 * @param {*} locationPreference - Provider's stated preferred location
 * @param {*} radiusPreference - Stated commute radius in miles
 * @param {*} fallbackLocation - Optional second source (e.g. a schedule block's locationText)
 * @returns {string} Pill label, never empty
 */
export const formatLocationPreference = (
  locationPreference,
  radiusPreference,
  fallbackLocation,
) => {
  const stated = cleanLocation(locationPreference);
  const fallback = cleanLocation(fallbackLocation);
  const place = hasRealLocation(stated)
    ? stated
    : hasRealLocation(fallback)
      ? fallback
      : '';

  const miles = parseInt(radiusPreference, 10);
  const radius = Number.isFinite(miles) && miles > 0
    ? (miles > 1000 ? 'Anywhere' : `${miles} mi`)
    : '';

  if (place) return radius ? `${place} (${radius})` : place;
  return radius ? `Location not specified (${radius})` : 'Location not specified';
};

export const AVAILABILITY_NOT_SPECIFIED = 'Availability not specified';
export const AVAILABILITY_NEEDS_CONFIRMATION = 'Availability needs confirmation';
export const LEGACY_AVAILABILITY_STALE_DAYS = 30;

export const computeAvailability = (dateStr) => {
  const unknown = AVAILABILITY_NOT_SPECIFIED;

  // A missing or unreadable legacy date is not evidence that somebody is
  // available today. Contact permission is a separate server-owned fact; this
  // label only describes how recently availability was confirmed.
  if (!dateStr) return unknown;
  
  try {
    const value = String(dateStr).trim();
    if (!value) return unknown;

    // These legacy tokens are an explicit provider statement, unlike a blank
    // or an expired date. Preserve the promise they actually made.
    if (/^(immediate(?:ly)?|available (?:immediately|now)|now)$/i.test(value)) {
      return 'Available Immediately';
    }

    // The stored value answers a calendar-date question even when legacy rows
    // serialize it as an ISO timestamp. Reading midnight UTC as an instant
    // makes the same stated date look one day old in Pacific time.
    const calendarDate = /^(\d{4})-(\d{2})-(\d{2})(?=$|[Tt])/.exec(value);
    if (calendarDate && /[Tt]/.test(value) && Number.isNaN(new Date(value).getTime())) {
      return unknown;
    }
    const d = calendarDate
      ? new Date(
          Number(calendarDate[1]),
          Number(calendarDate[2]) - 1,
          Number(calendarDate[3]),
        )
      : new Date(value);
    
    // Check if date is valid
    if (
      Number.isNaN(d.getTime()) ||
      (calendarDate &&
        (d.getFullYear() !== Number(calendarDate[1]) ||
          d.getMonth() !== Number(calendarDate[2]) - 1 ||
          d.getDate() !== Number(calendarDate[3])))
    ) {
      return unknown;
    }

    const today = new Date();
    // Matching owns this boundary in UTC. Use the stated ISO calendar prefix
    // directly so a browser west or east of UTC cannot disagree with the
    // server during the hours when their local calendar dates differ.
    const availabilityCalendarDay = calendarDate
      ? Date.UTC(
          Number(calendarDate[1]),
          Number(calendarDate[2]) - 1,
          Number(calendarDate[3]),
        )
      : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const todayCalendarDay = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
    );
    const diffDays = Math.round(
      (availabilityCalendarDay - todayCalendarDay) / (1000 * 60 * 60 * 24),
    );

    // Mirror the canonical matching window: a past start date remains current
    // for 30 days, then becomes unconfirmed. An old date is never an opt-out;
    // employers may still send an interest when the separate contactability
    // gate permits it.
    if (diffDays < -LEGACY_AVAILABILITY_STALE_DAYS) {
      return AVAILABILITY_NEEDS_CONFIRMATION;
    }
    
    if (diffDays <= 14) {
      return 'Available Immediately';
    }
    
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  } catch (error) {
    console.error('Error computing availability date:', error);
    return unknown;
  }
};

/**
 * The exact words the card's pay pill shows for a set of compensation values.
 *
 * Lifted out of ProfileFront so RateVisibilityRow can quote what a provider's
 * card actually reads ("Employers see $195/hr | $425K/yr") instead of asking them
 * to flip the switch and go look. Two copies of this formatting would drift, and
 * quoting the pill is the entire point of that sentence -- a paraphrase that
 * rounds $425K to "$425,000/yr" is already the bug.
 *
 * Withholding is deliberately NOT handled here. A withheld floor reaches the card
 * as `compensation.isBlind` on an already-sanitized profile with every amount
 * stripped, while RateVisibilityRow is the mirror case: it holds the raw
 * numbers and has to name them. So each caller owns its own "Competitive Rate"
 * branch and this returns only the visible-pay wording.
 */
export const buildCompensationLabel = (compensation) => {
  // Missing object and empty object are the same fact to a reader, and neither
  // is '—': that dash is what a populated compensation with no usable amount
  // renders, which is a different thing to explain.
  if (!compensation || Object.keys(compensation).length === 0) {
    return 'No rate specified';
  }

  // The live floors only (Provider/payFloors). Profiles use a type array and
  // service cards a single Hourly/Salary type; either way a deselected type's
  // amount is left off, and with no type every positive amount is a floor.
  const hourly = storedFloor({ compensation }, 'hourly', 'hourlyAmount');
  const salary = storedFloor({ compensation }, 'salary', 'salaryAmount');
  const parts = [];
  if (hourly) parts.push(`$${hourly}/hr`);
  if (salary) parts.push(`$${Math.round(salary / 1000)}K/yr`);

  // The legacy single amount, when no floor resolved. It reads as hourly
  // unless a type says otherwise.
  const { type: compType = 'hourly', amount: compAmt = 0 } = compensation;
  if (parts.length === 0 && compAmt) {
    parts.push(formatPay(compType, compAmt));
  }

  return parts.length > 0 ? parts.join(' | ') : '—';
};

/** Values buildCompensationLabel returns when there is no pay to quote. */
export const isQuotableCompensationLabel = (label) =>
  typeof label === 'string' &&
  label.trim() !== '' &&
  label !== '—' &&
  label !== 'No rate specified';
